<template>
  <ContentWrap>
    <el-form :model="query" inline>
      <el-form-item label="参与用户">
        <BusinessLookupSelect v-model="query.participantUserId" type="users" placeholder="请选择参与用户" />
      </el-form-item>
      <el-form-item label="阶段">
        <el-select v-model="query.stage" clearable class="!w-140px">
          <el-option
            v-for="(label, value) in stageLabels"
            :key="value"
            :label="label"
            :value="Number(value)"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="进行中" :value="1" />
          <el-option label="已关闭" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="创建时间">
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
        />
      </el-form-item>
    <el-form-item>
      <el-button type="primary" @click="search">查询</el-button>
    </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="roomNo" label="推进舱编号" min-width="150" />
      <el-table-column prop="title" label="主题" min-width="180" show-overflow-tooltip />
      <el-table-column label="负责人" min-width="120"><template #default="scope"><BusinessLookupName type="users" :id="scope.row.ownerUserId" /></template></el-table-column>
      <el-table-column label="阶段" width="100">
        <template #default="scope">{{ stageLabels[scope.row.stage] || '未知' }}</template>
      </el-table-column>
      <el-table-column prop="nextAction" label="下一步" min-width="160" show-overflow-tooltip />
      <el-table-column prop="nextFollowAt" label="下次跟进" width="180" />
      <el-table-column label="状态" width="90">
        <template #default="scope">{{ scope.row.status === 1 ? '进行中' : '已关闭' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="180">
        <template #default="scope">
          <el-button link type="primary" @click="openDetail(scope.row.id)">详情</el-button>
          <el-button
            v-if="scope.row.status === 1"
            v-hasPermi="['bosscircle:deal-room:update']"
            link
            :type="scope.row.stage === 6 ? 'success' : 'warning'"
            @click="changePaused(scope.row)"
          >
            {{ scope.row.stage === 6 ? '恢复' : '暂停' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="合作推进舱详情" width="900px">
    <el-descriptions v-if="detail?.room" :column="2" border>
      <el-descriptions-item label="推进舱编号">{{ detail.room.roomNo }}</el-descriptions-item>
      <el-descriptions-item label="阶段">{{ stageLabels[detail.room.stage] }}</el-descriptions-item>
      <el-descriptions-item label="负责人">{{ detail.room.ownerUserId }}</el-descriptions-item>
      <el-descriptions-item label="来源">{{ detail.room.sourceType }}</el-descriptions-item>
      <el-descriptions-item label="参与用户" :span="2">{{
        detail.room.participantJson || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="下一步" :span="2">{{
        detail.room.nextAction || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="会议纪要" :span="2">{{
        detail.room.meetingSummary || '-'
      }}</el-descriptions-item>
    </el-descriptions>
    <el-divider content-position="left">待办</el-divider>
    <el-table :data="detail?.tasks || []" max-height="240">
      <el-table-column prop="title" label="标题" min-width="160" />
      <el-table-column label="负责人" min-width="120"><template #default="scope"><BusinessLookupName type="users" :id="scope.row.assigneeUserId" /></template></el-table-column>
      <el-table-column prop="status" label="状态" width="80" />
      <el-table-column prop="dueTime" label="截止时间" width="180" />
    </el-table>
    <el-divider content-position="left">阶段日志</el-divider>
    <el-table :data="detail?.stageLogs || []" max-height="240">
      <el-table-column prop="fromStage" label="原阶段" width="90" />
      <el-table-column prop="toStage" label="目标阶段" width="90" />
      <el-table-column prop="changeReason" label="原因" min-width="180" />
      <el-table-column prop="createTime" label="时间" width="180" />
    </el-table>
  </Dialog>
</template>

<script setup lang="ts">
import * as DealRoomApi from '@/api/business/dealRoom'

defineOptions({ name: 'BossCircleDealRoom' })
const message = useMessage()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const timeRange = ref<string[]>([])
const detailVisible = ref(false)
const detail = ref<any>()
const stageLabels: Record<number, string> = {
  0: '新建',
  1: '确认',
  2: '会谈',
  3: '方案',
  4: '成交',
  5: '失败',
  6: '暂停'
}
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  participantUserId: undefined,
  stage: undefined,
  status: undefined
})
const load = async () => {
  loading.value = true
  try {
    const data = await DealRoomApi.getDealRoomPage({
      ...query,
      beginTime: timeRange.value?.[0],
      endTime: timeRange.value?.[1]
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const search = () => {
  query.pageNo = 1
  load()
}
const openDetail = async (id: number) => {
  detail.value = await DealRoomApi.getDealRoom(id)
  detailVisible.value = true
}
const changePaused = async (row: any) => {
  const paused = row.stage !== 6
  const { value } = await message.prompt(
    `请输入${paused ? '暂停' : '恢复'}原因`,
    paused ? '暂停推进舱' : '恢复推进舱'
  )
  await DealRoomApi.updateDealRoomPaused({
    id: row.id,
    version: row.version,
    paused,
    reason: value
  })
  message.success(paused ? '已暂停' : '已恢复')
  await load()
}
onMounted(load)
</script>
